import type { ReactNode } from 'react';
import type { Lang } from './lang.js';

/**
 * Every string on the unauthenticated screens: sign in, register, the password
 * flow and email verification.
 *
 * These are the pages someone reaches from an email link, often before they
 * have ever seen the product — so the language being wrong here is the first
 * impression, not a detail.
 */
export interface AuthCopy {
  login: {
    signInTitle: string;
    registerTitleSaas: string;
    registerTitleSingle: string;
    continueWithGoogle: string;
    or: string;
    orgName: string;
    email: string;
    password: string;
    signIn: string;
    createAccount: string;
    forgot: string;
    noAccount: string;
    haveAccount: string;
    register: string;
    signInLink: string;
    registrationClosed: string;
    somethingWrong: string;
    googleFailed: string;
    ssoButton: string;
    ssoContinue: string;
    ssoEnforced: string;
    ssoEnterEmail: string;
    ssoNotConfigured: string;
    ssoFailed: string;
  };
  forgot: {
    title: string;
    body: string;
    email: string;
    send: string;
    sentTitle: string;
    /** Takes the address so it can be emphasised in place. */
    sentBody: (email: string) => ReactNode;
    back: string;
  };
  reset: {
    title: string;
    welcomeTitle: string;
    welcomeBody: string;
    password: string;
    newPassword: string;
    confirm: string;
    helper: string;
    submit: string;
    welcomeSubmit: string;
    tooShort: string;
    mismatch: string;
    badToken: string;
    badInvite: string;
    doneTitle: string;
    welcomeDoneTitle: string;
    doneBody: string;
    welcomeDoneBody: string;
    goToSignIn: string;
    noTokenTitle: string;
    noTokenBody: string;
    noTokenInvite: string;
    requestNew: string;
  };
  verify: {
    checking: string;
    okTitle: string;
    okBody: string;
    failedTitle: string;
    failedBody: string;
    goToDashboard: string;
    goToSignIn: string;
  };
}

export const AUTH_COPY: Record<Lang, AuthCopy> = {
  en: {
    login: {
      signInTitle: 'Sign in to your account',
      registerTitleSaas: 'Create your organization',
      registerTitleSingle: 'Create the administrator account',
      continueWithGoogle: 'Continue with Google',
      or: 'or',
      orgName: 'Organization name',
      email: 'Email',
      password: 'Password',
      signIn: 'Sign in',
      createAccount: 'Create account',
      forgot: 'Forgot password?',
      noAccount: "Don't have an account? ",
      haveAccount: 'Already have an account? ',
      register: 'Register',
      signInLink: 'Sign in',
      registrationClosed: 'Registration is closed. Ask an administrator to invite you.',
      somethingWrong: 'Something went wrong',
      googleFailed: 'Google sign-in was cancelled or failed. Please try again.',
      ssoButton: 'Sign in with SSO',
      ssoContinue: 'Continue with single sign-on',
      ssoEnforced: 'Your organization signs in through its own identity provider.',
      ssoEnterEmail: 'Enter your work email address first.',
      ssoNotConfigured: 'Single sign-on is not configured for this email domain.',
      ssoFailed: 'Single sign-on failed. Please try again.',
    },
    forgot: {
      title: 'Reset your password',
      body: "Enter your account email and we'll send you a reset link.",
      email: 'Email',
      send: 'Send reset link',
      sentTitle: 'Check your inbox',
      sentBody: (email) => (
        <>
          If an account exists for <b>{email}</b>, we&apos;ve sent a link to reset your password. It
          expires in one hour.
        </>
      ),
      back: 'Back to sign in',
    },
    reset: {
      title: 'Choose a new password',
      welcomeTitle: 'Set your password',
      welcomeBody:
        'An account was created for you. Choose a password to activate it. Only you ever see it.',
      password: 'Password',
      newPassword: 'New password',
      confirm: 'Confirm password',
      helper: 'At least 8 characters',
      submit: 'Update password',
      welcomeSubmit: 'Activate account',
      tooShort: 'Password must be at least 8 characters.',
      mismatch: 'Passwords do not match.',
      badToken: 'This reset link is invalid or has expired. Request a new one.',
      badInvite: 'This invitation link is invalid or has expired. Ask an administrator to resend it.',
      doneTitle: 'Password updated',
      welcomeDoneTitle: 'Account activated',
      doneBody: 'You can now sign in with your new password.',
      welcomeDoneBody: 'Your account is ready. Sign in with the password you just chose.',
      goToSignIn: 'Go to sign in',
      noTokenTitle: 'Invalid link',
      noTokenBody: 'This link is missing its token.',
      noTokenInvite: 'Ask an administrator to resend your invitation.',
      requestNew: 'Request a new link',
    },
    verify: {
      checking: 'Verifying your email…',
      okTitle: 'Email verified',
      okBody: 'Your email address is confirmed. You now have full access to ReplayOps.',
      failedTitle: 'Link invalid or expired',
      failedBody:
        'This verification link is no longer valid. Sign in and use the “Resend email” button to get a fresh link.',
      goToDashboard: 'Go to the dashboard',
      goToSignIn: 'Go to sign in',
    },
  },
  tr: {
    login: {
      signInTitle: 'Hesabınıza giriş yapın',
      registerTitleSaas: 'Organizasyonunuzu oluşturun',
      registerTitleSingle: 'Yönetici hesabını oluşturun',
      continueWithGoogle: 'Google ile devam et',
      or: 'veya',
      orgName: 'Organizasyon adı',
      email: 'E-posta',
      password: 'Parola',
      signIn: 'Giriş yap',
      createAccount: 'Hesap oluştur',
      forgot: 'Parolamı unuttum',
      noAccount: 'Hesabınız yok mu? ',
      haveAccount: 'Zaten hesabınız var mı? ',
      register: 'Kayıt ol',
      signInLink: 'Giriş yap',
      registrationClosed: 'Kayıt kapalı. Bir yöneticiden sizi davet etmesini isteyin.',
      somethingWrong: 'Bir şeyler ters gitti',
      googleFailed: 'Google ile giriş iptal edildi ya da başarısız oldu. Lütfen tekrar deneyin.',
      ssoButton: 'SSO ile giriş yap',
      ssoContinue: 'Tek oturum açma ile devam et',
      ssoEnforced: 'Organizasyonunuz kendi kimlik sağlayıcısı üzerinden giriş yapıyor.',
      ssoEnterEmail: 'Önce kurumsal e-posta adresinizi girin.',
      ssoNotConfigured: 'Bu e-posta alan adı için tek oturum açma yapılandırılmamış.',
      ssoFailed: 'Tek oturum açma başarısız oldu. Lütfen tekrar deneyin.',
    },
    forgot: {
      title: 'Parolanızı sıfırlayın',
      body: 'Hesap e-postanızı girin, size bir sıfırlama bağlantısı gönderelim.',
      email: 'E-posta',
      send: 'Sıfırlama bağlantısı gönder',
      sentTitle: 'Gelen kutunuza bakın',
      sentBody: (email) => (
        <>
          <b>{email}</b> adresine ait bir hesap varsa, parolanızı sıfırlamanız için bir bağlantı
          gönderdik. Bağlantı bir saat içinde geçerliliğini yitirir.
        </>
      ),
      back: 'Girişe dön',
    },
    reset: {
      title: 'Yeni bir parola seçin',
      welcomeTitle: 'Parolanızı belirleyin',
      welcomeBody:
        'Sizin için bir hesap oluşturuldu. Etkinleştirmek için bir parola seçin. Onu yalnızca siz görürsünüz.',
      password: 'Parola',
      newPassword: 'Yeni parola',
      confirm: 'Parolayı doğrulayın',
      helper: 'En az 8 karakter',
      submit: 'Parolayı güncelle',
      welcomeSubmit: 'Hesabı etkinleştir',
      tooShort: 'Parola en az 8 karakter olmalı.',
      mismatch: 'Parolalar birbiriyle uyuşmuyor.',
      badToken: 'Bu sıfırlama bağlantısı geçersiz ya da süresi dolmuş. Yenisini isteyin.',
      badInvite:
        'Bu davet bağlantısı geçersiz ya da süresi dolmuş. Bir yöneticiden yeniden göndermesini isteyin.',
      doneTitle: 'Parola güncellendi',
      welcomeDoneTitle: 'Hesap etkinleştirildi',
      doneBody: 'Artık yeni parolanızla giriş yapabilirsiniz.',
      welcomeDoneBody: 'Hesabınız hazır. Az önce seçtiğiniz parolayla giriş yapın.',
      goToSignIn: 'Girişe git',
      noTokenTitle: 'Geçersiz bağlantı',
      noTokenBody: 'Bu bağlantıda belirteç eksik.',
      noTokenInvite: 'Bir yöneticiden davetinizi yeniden göndermesini isteyin.',
      requestNew: 'Yeni bağlantı iste',
    },
    verify: {
      checking: 'E-postanız doğrulanıyor…',
      okTitle: 'E-posta doğrulandı',
      okBody: 'E-posta adresiniz onaylandı. Artık ReplayOps\'a tam erişiminiz var.',
      failedTitle: 'Bağlantı geçersiz ya da süresi dolmuş',
      failedBody:
        'Bu doğrulama bağlantısı artık geçerli değil. Giriş yapıp “E-postayı yeniden gönder” düğmesiyle yenisini alın.',
      goToDashboard: 'Panele git',
      goToSignIn: 'Girişe git',
    },
  },
};
